/**
 * Гостевая сессия: `POST/GET/DELETE /api/v1/guest/session` (BE-205, FE-205).
 *
 * Токен гостя живёт в HttpOnly cookie и в JavaScript недоступен — здесь только
 * состояние: единственный заказ, остаток сообщений и срок жизни подбора.
 * Идентификатора сессии в ответе нет и быть не может: он немедленно стал бы
 * вторым способом адресации заказа (MVP_PRD §41.4).
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiFetch } from '@shared/api/client';
import { ApiError } from '@shared/api/problem';
import { queryKeys } from '@shared/api/queryKeys';

/** Ответ `/guest/session`. */
export interface GuestSessionState {
  /** Единственный заказ гостя; `null` — ещё не создан. */
  search_session_id: string | null;
  messages_left: number;
  message_limit: number;
  /** ISO 8601 — срок жизни подбора **в этом браузере**. */
  expires_at: string;
}

export const GUEST_STALE_TIME_MS = 60_000;

/**
 * Состояние гостевой сессии или `null`.
 *
 * `401` — штатный ответ «гостевой сессии нет», а `404` — «гостевой контур
 * выключен». Оба означают «гостя нет» и не должны выглядеть как сбой: иначе
 * зарегистрированный пользователь видел бы ошибку на каждом экране.
 */
export function useGuestSession(enabled = true): UseQueryResult<GuestSessionState | null> {
  return useQuery({
    queryKey: queryKeys.guestSession(),
    queryFn: async () => {
      try {
        return await apiFetch<GuestSessionState>('/guest/session');
      } catch (error) {
        if (error instanceof ApiError && (error.status === 401 || error.status === 404)) {
          return null;
        }
        throw error;
      }
    },
    enabled,
    staleTime: GUEST_STALE_TIME_MS,
    retry: false,
  });
}

/**
 * Начать подбор без регистрации.
 *
 * Идемпотентно по cookie: действующая сессия не заменяется новой, поэтому
 * повторный вызов из второй вкладки не стирает начатый диалог.
 */
export function useStartGuestSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiFetch<GuestSessionState>('/guest/session', { method: 'POST' }),
    onSuccess: (state) => {
      queryClient.setQueryData(queryKeys.guestSession(), state);
    },
  });
}

/**
 * Сбросить подбор и начать заново (MVP_PRD §41.2).
 *
 * Диалог, критерии и результаты удаляются — это то же удаление заказа, что и у
 * зарегистрированного. Кеш чистится целиком: в нём лежат данные заказа,
 * которого больше нет.
 */
export function useResetGuestSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiFetch<GuestSessionState>('/guest/session', { method: 'DELETE' }),
    onSuccess: async (state) => {
      await queryClient.resetQueries();
      queryClient.setQueryData(queryKeys.guestSession(), state);
    },
  });
}
